// Resolve a quota breach.
// Endpoint: POST /api/admin/licensing/breaches/:id/resolve
// Operator-issued states only: resolved_extended | resolved_paid
// (resolved_hard_cap is daemon-owned).

import React, { useEffect, useState } from "react";
import {
  Dialog,
  Box,
  Stack,
  Typography,
  Button,
  CircularProgress,
  RadioGroup,
  FormControlLabel,
  Radio,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { TOKENS } from "../design";
import { resolveBreach } from "../../../../../services/licensingService";

const M_ResolveBreach = ({ open, breach, orgTitle, onClose, onConfirmed }) => {
  const [state, setState] = useState("resolved_paid");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!open) return;
    setState("resolved_paid");
    setSaving(false);
    setErr(null);
  }, [open]);

  const handleConfirm = async () => {
    if (!breach || saving) return;
    setSaving(true);
    setErr(null);
    try {
      await resolveBreach(breach.id, state);
      onConfirmed?.();
    } catch (e) {
      setErr(e?.response?.data?.message || e?.message || "Failed to resolve breach");
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={!!open}
      onClose={saving ? undefined : onClose}
      PaperProps={{
        sx: {
          borderRadius: `${TOKENS.radiusM}px`,
          boxShadow: TOKENS.shadowL,
          maxWidth: 480,
          width: "100%",
          background: TOKENS.bgSurface,
        },
      }}
    >
      <Box sx={{ p: "20px 22px 18px" }}>
        <Stack direction="row" spacing={1.75}>
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: `${TOKENS.radiusXs}px`,
              background: TOKENS.successBg,
              color: TOKENS.successInk,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <CheckCircleOutlineIcon />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: 17, fontWeight: 700, color: TOKENS.fg1, letterSpacing: "-0.01em" }}>
              Resolve quota breach
            </Typography>
            <Typography sx={{ fontSize: 13.5, color: TOKENS.fg2, mt: 0.75, lineHeight: 1.5 }}>
              Resolving the <strong>{breach?.dimension}</strong> breach on{" "}
              <strong>{breach?.sku_id}</strong> for <strong>{orgTitle}</strong>. This clears the 60-day
              hard-cap timer; the daemon may re-open the breach if usage continues over quota.
            </Typography>
          </Box>
        </Stack>

        <Box sx={{ mt: 2 }}>
          <Typography
            sx={{
              fontSize: 11.5,
              fontWeight: 700,
              color: TOKENS.fg3,
              textTransform: "uppercase",
              letterSpacing: "0.06em",
              mb: 0.75,
            }}
          >
            Resolution
          </Typography>
          <RadioGroup value={state} onChange={(e) => setState(e.target.value)}>
            <FormControlLabel
              value="resolved_paid"
              control={<Radio size="small" />}
              label={
                <Box>
                  <Typography sx={{ fontSize: 13, color: TOKENS.fg1, fontWeight: 600 }}>
                    Paid by customer
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: TOKENS.fg3 }}>
                    The customer has purchased additional capacity / a new SKU.
                  </Typography>
                </Box>
              }
            />
            <FormControlLabel
              value="resolved_extended"
              control={<Radio size="small" />}
              label={
                <Box>
                  <Typography sx={{ fontSize: 13, color: TOKENS.fg1, fontWeight: 600 }}>
                    Extended by admin
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: TOKENS.fg3 }}>
                    Operator-granted grace; quota may still be exceeded.
                  </Typography>
                </Box>
              }
            />
          </RadioGroup>
        </Box>

        {err ? (
          <Box
            sx={{
              mt: 2,
              p: 1.25,
              borderRadius: `${TOKENS.radiusXs}px`,
              background: TOKENS.dangerBg,
              border: `1px solid ${TOKENS.dangerBorder}`,
              color: TOKENS.dangerInk,
              fontSize: 12.5,
            }}
          >
            {err}
          </Box>
        ) : null}

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 2.5 }}>
          <Button
            onClick={onClose}
            disabled={saving}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              color: TOKENS.fg1,
              background: "transparent",
              border: `1px solid ${TOKENS.border}`,
              borderRadius: `${TOKENS.radiusXs}px`,
              px: 2,
              "&:hover": { background: TOKENS.bgSunken },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={saving || !breach}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              color: "#fff",
              background: TOKENS.success,
              borderRadius: `${TOKENS.radiusXs}px`,
              px: 2,
              minWidth: 96,
              "&:hover": { background: "#179B57" },
              "&.Mui-disabled": { color: "#fff", opacity: 0.55 },
            }}
          >
            {saving ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : "Resolve"}
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};

export default M_ResolveBreach;
